import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';

const formatINR = (amount) => {
  if (!amount) return '₹0';
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount.toLocaleString('en-IN')}`;
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-slate-400',
};

export default function NeedsAttention({ onQuickLog }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [collapsed, setCollapsed] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/needs-attention`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch needs-attention data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4" data-testid="needs-attention-loading">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
            <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.summary?.total_items === 0) {
    return (
      <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 p-4" data-testid="needs-attention-empty">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">All clear!</p>
            <p className="text-xs text-emerald-600 dark:text-emerald-400">No items need your attention right now</p>
          </div>
        </div>
      </div>
    );
  }

  const { stale_leads, overdue_tasks, at_risk_opportunities, summary } = data;

  const tabs = [
    { id: 'all', label: 'All', count: summary.total_items },
    { id: 'stale', label: 'Stale Leads', count: summary.stale_count },
    { id: 'overdue', label: 'Overdue Tasks', count: summary.overdue_count },
    { id: 'risk', label: 'At Risk', count: summary.at_risk_count },
  ];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-red-200 dark:border-red-900/50 overflow-hidden" data-testid="needs-attention-panel">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-red-50 to-amber-50 dark:from-red-900/20 dark:to-amber-900/20 border-b border-red-100 dark:border-red-900/30"
        data-testid="needs-attention-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-red-100 dark:bg-red-900/40 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">
              Needs Attention
              <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full">
                {summary.total_items}
              </span>
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {summary.stale_count} stale leads · {summary.overdue_count} overdue tasks · {formatINR(summary.money_at_risk)} at risk
            </p>
          </div>
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${collapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {!collapsed && (
        <div>
          {/* Tabs */}
          <div className="flex gap-1 px-3 pt-3 pb-2 border-b border-slate-100 dark:border-slate-700 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                data-testid={`needs-attention-tab-${tab.id}`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
                    activeTab === tab.id 
                      ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900' 
                      : 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700">
            {/* Stale Leads */}
            {(activeTab === 'all' || activeTab === 'stale') && stale_leads.map(lead => (
              <div key={lead.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group" data-testid={`stale-lead-${lead.id}`}>
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/leads/${lead.id}`} className="text-sm font-medium text-slate-800 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 truncate block">
                    {lead.name}
                  </Link>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {lead.organization || 'No company'} · {lead.days_stale} days since last activity
                  </p>
                </div>
                {lead.estimated_value > 0 && (
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatINR(lead.estimated_value)}</span>
                )}
                <button
                  onClick={() => onQuickLog && onQuickLog({ lead_id: lead.id, lead_name: lead.name, activity_type: 'call' })}
                  className="opacity-0 group-hover:opacity-100 px-2 py-1 text-xs font-medium bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-md hover:bg-slate-700 dark:hover:bg-slate-300 transition-all whitespace-nowrap"
                  data-testid={`quick-log-stale-${lead.id}`}
                >
                  Log Activity
                </button>
              </div>
            ))}

            {/* Overdue Tasks */}
            {(activeTab === 'all' || activeTab === 'overdue') && overdue_tasks.map(task => (
              <div key={task.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors" data-testid={`overdue-task-${task.id}`}>
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <Link to="/tasks" className="text-sm font-medium text-slate-800 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 truncate block">
                    {task.title}
                  </Link>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {task.days_overdue} days overdue
                    {task.lead_name && ` · ${task.lead_name}`}
                  </p>
                </div>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`} title={task.priority}></div>
              </div>
            ))}

            {/* At-Risk Opportunities */}
            {(activeTab === 'all' || activeTab === 'risk') && at_risk_opportunities.map(opp => (
              <div key={opp.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors" data-testid={`at-risk-opp-${opp.id}`}>
                <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/opportunities/${opp.id}`} className="text-sm font-medium text-slate-800 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 truncate block">
                    {opp.name}
                  </Link>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {opp.risk_reasons?.[0]} · Stage: {opp.stage}
                  </p>
                </div>
                <span className="text-xs font-bold text-amber-600 dark:text-amber-400 whitespace-nowrap">{formatINR(opp.deal_value)}</span>
              </div>
            ))}

            {/* Empty state for filtered tabs */}
            {activeTab === 'stale' && stale_leads.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-slate-400">No stale leads</div>
            )}
            {activeTab === 'overdue' && overdue_tasks.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-slate-400">No overdue tasks</div>
            )}
            {activeTab === 'risk' && at_risk_opportunities.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-slate-400">No at-risk opportunities</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
